import { getWebHostResourcesId, HostResourceResponse } from "api"
import { useEffect, useState } from "react"

export function useHostResource(hostResourceId: number | undefined) {
  const [resource, setResource] = useState<HostResourceResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!hostResourceId) return

    let cancelled = false
    setLoading(true)
    setError(false)

    getWebHostResourcesId(String(hostResourceId))
      .then((res) => {
        if (cancelled) return
        if (res.data?.body) {
          setResource(res.data.body)
        } else {
          setError(true)
        }
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hostResourceId])

  return { resource, loading, error }
}

/**
 * Maps HOST day names to JS day indices (0=Sunday).
 */
const DAY_MAP: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
}

/**
 * Returns a shouldDisableDate function for MUI DatePicker
 * that disables days not in the resource's availableDays.
 */
export function getShouldDisableDate(availableDays: string[] | undefined) {
  if (!availableDays || availableDays.length === 0) return undefined
  const allowedDays = new Set(availableDays.map((d) => DAY_MAP[d]).filter((d) => d !== undefined))
  return (date: any) => !allowedDays.has(date.day())
}

/**
 * Converts HOST decimal hours (e.g., 8.5 = 08:30) to time slots
 * and filters TIME_SLOTS to only include times within the range.
 */
export function filterTimeSlotsToRange(
  timeSlots: string[],
  availableFrom: number | undefined,
  availableTo: number | undefined,
): string[] {
  if (availableFrom === undefined && availableTo === undefined) return timeSlots

  return timeSlots.filter((slot) => {
    const [h, m] = slot.split(":").map(Number)
    const decimal = h + m / 60
    if (availableFrom !== undefined && decimal < availableFrom) return false
    if (availableTo !== undefined && decimal >= availableTo) return false
    return true
  })
}
